import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { cn } from '@plato/ui/util/classes';
import { addDays, eachDayOfInterval, formatISO, intlFormat, parseISO, startOfWeek } from 'date-fns';
import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';

export type Props = {
    day: string;
    onPick: (day: string) => void;
    className?: string;
};

type DayTileProps = {
    date: Date;
    isSelected: boolean;
    isToday: boolean;
    isFocusable: boolean;
    onPick: () => void;
};

function DayTile({ date, isSelected, isToday, isFocusable, onPick }: DayTileProps): ReactNode {
    const locale = useResolvedLocale();

    return (
        <button
            type="button"
            aria-pressed={isSelected}
            tabIndex={isFocusable ? undefined : -1}
            onClick={onPick}
            className={cn(
                'flex flex-1 flex-col items-center gap-0.5 py-1.5 select-none',
                'rounded-default border border-transparent bg-neutral-100 text-content',
                'transition-colors duration-state ease-state not-aria-pressed:hover:bg-content/(--opacity-wash-hover) active:translate-px',
                // The selected look of FilterPill
                'aria-pressed:border-blue-300 aria-pressed:bg-blue-50 aria-pressed:text-blue-700',
            )}
        >
            <span
                className={cn(
                    'text-caption',
                    isToday && 'font-bold',
                    isSelected || isToday ? 'text-brand' : 'text-content-weak',
                )}
            >
                {intlFormat(date, { weekday: 'short' }, { locale })}
            </span>
            <span className={cn('text-body-emphasis', isToday && !isSelected && 'text-brand')}>
                {intlFormat(date, { day: 'numeric' }, { locale })}
            </span>
        </button>
    );
}

// The previous, current and next week, so the neighbours slide in while scrolling
const weekOffsets = [-1, 0, 1];

// Without `scrollend` (older Safari), this long without a scroll event means the snap has settled
const scrollSettleMs = 120;
const supportsScrollEnd = 'onscrollend' in window;

/**
 * The selected day's week as a row of tiles; tapping one picks that day. Scrolling the row sideways
 * pages a week and keeps the weekday.
 */
export function DayStrip({ day, onPick, className }: Props): ReactNode {
    const today = formatISO(new Date(), { representation: 'date' });
    const date = parseISO(day);
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    const weekStartIso = formatISO(weekStart, { representation: 'date' });

    const scrollRef = useRef<HTMLDivElement>(null);
    const paneRefs = useRef<(HTMLDivElement | null)[]>([]);

    // Show the current week, also after paging, when the old neighbour has become the middle one
    useLayoutEffect(() => {
        const scroller = scrollRef.current;
        const currentPane = paneRefs.current[weekOffsets.indexOf(0)];
        if (scroller && currentPane) {
            scroller.scrollLeft = currentPane.offsetLeft;
        }
    }, [weekStartIso]);

    // Once a scroll has come to rest, pick the same weekday in the week it rests on
    useEffect(() => {
        const scroller = scrollRef.current;
        if (!scroller) {
            return;
        }

        // The week nearest the scroll position: an exact match can miss by a fraction of a pixel
        const pickRestingWeek = (): void => {
            const distances = paneRefs.current.map((pane) =>
                pane ? Math.abs(pane.offsetLeft - scroller.scrollLeft) : Number.POSITIVE_INFINITY,
            );
            const weekOffset = weekOffsets[distances.indexOf(Math.min(...distances))] ?? 0;
            if (weekOffset !== 0) {
                onPick(formatISO(addDays(parseISO(day), weekOffset * 7), { representation: 'date' }));
            }
        };

        if (supportsScrollEnd) {
            scroller.addEventListener('scrollend', pickRestingWeek);
            return () => {
                scroller.removeEventListener('scrollend', pickRestingWeek);
            };
        }

        let settleTimeout: ReturnType<typeof setTimeout> | undefined;
        const waitForRest = (): void => {
            clearTimeout(settleTimeout);
            settleTimeout = setTimeout(pickRestingWeek, scrollSettleMs);
        };
        scroller.addEventListener('scroll', waitForRest);
        return () => {
            scroller.removeEventListener('scroll', waitForRest);
            clearTimeout(settleTimeout);
        };
    }, [day, onPick]);

    return (
        <div className={className}>
            <div
                ref={scrollRef}
                // A gutter between the weeks so the seam reads while scrolling
                className="flex snap-x snap-mandatory gap-relaxed overflow-x-auto overscroll-x-contain scrollbar-none"
            >
                {weekOffsets.map((weekOffset, paneIndex) => {
                    const weekStartOfPane = addDays(weekStart, weekOffset * 7);
                    const isCurrent = weekOffset === 0;
                    // Monday to Friday, as in the week view and the date picker
                    const weekDays = eachDayOfInterval({
                        start: weekStartOfPane,
                        end: addDays(weekStartOfPane, 4),
                    });

                    return (
                        <div
                            key={formatISO(weekStartOfPane, { representation: 'date' })}
                            ref={(pane) => {
                                paneRefs.current[paneIndex] = pane;
                            }}
                            aria-hidden={!isCurrent}
                            className="flex w-full shrink-0 snap-center snap-always gap-cozy"
                        >
                            {weekDays.map((weekDay) => {
                                const iso = formatISO(weekDay, { representation: 'date' });

                                return (
                                    <DayTile
                                        key={iso}
                                        date={weekDay}
                                        isSelected={iso === day}
                                        isToday={iso === today}
                                        isFocusable={isCurrent}
                                        onPick={() => {
                                            onPick(iso);
                                        }}
                                    />
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
